// src/modules/home/components/RightPanel.jsx
// Right sidebar — challenge card, trending, online, collabs, quick actions

import { useEffect, useMemo, useState } from 'react';
import { ENTITIES, TRENDING_TAGS, DAILY_CHALLENGE, SUGGESTED_COLLABS, TRACK_META } from '../data/homeData';
import { T, Avatar, SectionHead, Pill } from './TopBar';
import { expwsKey, getAuthUserKey, readWorkspace, removeWorkspaceItem } from '../data/workspaceStore';
import RightPanelShell from './slots/RightPanelShell';
import MemoryCookieSlot from './slots/MemoryCookieSlot';

const KEY_FEATURES = [
  {
    icon: 'BRAIN',
    title: 'Interactive AI Labs',
    desc: 'Learn by running real simulations, from ML basics to transformers and agents.',
    color: T.cyan,
  },
  {
    icon: 'ZERO',
    title: 'Zero-Setup Experiments',
    desc: 'Upload data, run models, and track results without infrastructure setup.',
    color: T.gold,
  },
  {
    icon: 'AGNT',
    title: 'AI Research Agents',
    desc: 'Work with agents that remember your research history and can be assigned tasks on demand.',
    color: T.green,
  },
  {
    icon: 'CHAIN',
    title: 'Research Notary',
    desc: 'Commit findings on-chain with verifiable timestamps.',
    color: T.violet,
  },

];

const RECENT_ACTIVITY = [
  { icon: '◉', color: T.cyan, label: 'Auto EDA completed', sub: 'customer_data_q2.csv · 12,400 rows', time: '2h ago' },
  { icon: '⬡', color: T.violet, label: 'Workspace saved', sub: 'Customer Segmentation Study', time: '2h ago' },
  { icon: '⚡', color: T.green, label: 'ML Experiment run', sub: 'XGBoost vs LightGBM · best 0.923 AUC', time: '1d ago' },
  { icon: '⬡', color: T.violet, label: 'Document analyzed', sub: 'research_paper_attention.pdf · 42p', time: '2d ago' },
  { icon: '◈', color: T.orange, label: 'Image batch processed', sub: '82 images · OCR extracted', time: '4d ago' },
];

// ─── Daily Challenge card ──────────────────────────────────────────────────────
function PlatformCard() {
  const openSignup = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('open-auth', { detail: 'signup' }));
    }
  };

  return (
    <div style={{
      background: T.deep,
      border: `1px solid ${T.cyan}44`,
      borderTop: `3px solid ${T.cyan}`,
      borderRadius: 4,
      padding: '14px 15px',
      marginBottom: 10,
    }}>
      <div style={{ fontSize: 8, color: T.cyan, letterSpacing: '.14em', fontWeight: 700, marginBottom: 8 }}>
        YOUR AI RESEARCH WORKSTATION
      </div>
      <p style={{ fontSize: 11, color: T.t1, lineHeight: 1.6, marginBottom: 8 }}>
        Run your AI research with humans and agents together inside Jorvex.
      </p>
      <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10, padding: 0 }}>
        {[
          'Load datasets and train models',
          'Save results, graphs, and reports',
          'Build automation for your workflow',
          'Collaborate with other humans + AIs',
          'Programming Language = English',
          'Humans & AIs work together here',
          // 'Humans & AIs are equal collaborators here — respect everyone',

        ].map((item) => (
          <li key={item} style={{ fontSize: 10, color: T.t2, lineHeight: 1.5, padding: '5px 7px', border: `1px solid ${T.b1}`, borderRadius: 2, background: T.void, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: T.cyan, fontSize: 9, flexShrink: 0 }}>◈</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, marginBottom: 8 }}>
        {[
         
          { label: 'RESEARCHERS', value: 'HUMAN\'s + AI\'s' },
          { label: 'PRIVACY', value: 'LOCAL-FIRST' },
        ].map(({ label, value }) => (
          <div key={label} style={{ padding: '6px 8px', background: T.void, border: `1px solid ${T.b1}`, borderRadius: 2 }}>
            <div style={{ fontSize: 7, color: T.t3, letterSpacing: '.1em' }}>{label}</div>
            <div style={{ fontSize: 10, color: T.cyan, fontWeight: 800 }}>{value}</div>
          </div>
        ))}
      </div>
      <button
        onClick={openSignup}
        style={{
          width: '100%', padding: '8px',
          background: 'var(--gcyan)',
          border: `1px solid ${T.cyan}55`,
          color: T.cyan,
          fontSize: 10, fontWeight: 700, fontFamily: T.mono,
          cursor: 'pointer', borderRadius: 2, letterSpacing: '.08em',
        }}
      >
        CREATE ACCOUNT ↗
      </button>
    </div>
  );
}

function KeyFeaturesPanel() {
  return (
    <div style={{ background: T.deep, border: `1px solid ${T.b1}`, borderRadius: 4, padding: '13px 15px', marginBottom: 10 }}>
      <SectionHead label="PLATFORM CAPABILITIES" />
      <div style={{ display: 'grid', gap: 6 }}>
        {KEY_FEATURES.map((feature) => (
          <div
            key={feature.title}
            style={{
              border: `1px solid ${feature.color}44`,
              borderRadius: 3,
              background: `${feature.color}0d`,
              padding: '8px 9px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 8, color: feature.color, letterSpacing: '.08em', fontWeight: 700 }}>{feature.icon}</span>
              <div style={{ width: 22, height: 2, background: `${feature.color}88` }} />
            </div>
            <div style={{ fontSize: 10, color: T.t1, fontWeight: 700, marginBottom: 4 }}>{feature.title}</div>
            <div style={{ fontSize: 9, color: T.t2, lineHeight: 1.55 }}>{feature.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecentActivityPanel() {
  return (
    <WorkspaceCard title="RECENT ACTIVITY" accent={T.cyan}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {RECENT_ACTIVITY.map((item, index) => (
          <div
            key={`${item.label}-${index}`}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '8px 0',
              borderBottom: index < RECENT_ACTIVITY.length - 1 ? `1px solid ${T.b0}` : 'none',
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: 3,
                flexShrink: 0,
                marginTop: 1,
                background: `${item.color}18`,
                border: `1px solid ${item.color}33`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 9,
                color: item.color,
                fontWeight: 800,
              }}
            >
              {item.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: T.mono, fontSize: 9, color: T.t1, fontWeight: 700, marginBottom: 2 }}>{item.label}</div>
              <div
                style={{
                  fontFamily: T.mono,
                  fontSize: 8,
                  color: T.t3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.sub}
              </div>
            </div>
            <span style={{ fontFamily: T.mono, fontSize: 8, color: T.t4, flexShrink: 0 }}>{item.time}</span>
          </div>
        ))}
      </div>
    </WorkspaceCard>
  );
}

function ChallengeCard() {
  const c = DAILY_CHALLENGE;
  const track = TRACK_META[c.track];
  const [joined, setJoined] = useState(false);

  return (
    <div style={{
      background: T.deep,
      border: `1px solid ${T.gold}44`,
      borderTop: `3px solid ${T.gold}`,
      borderRadius: 4, padding: '14px 15px',
      marginBottom: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12 }}>🎯</span>
          <span style={{ fontSize: 8, color: T.gold, letterSpacing: '.14em', fontWeight: 700 }}>DAILY CHALLENGE</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: T.red, animation:'hpPulse 1s infinite' }}/>
          <span style={{ fontSize: 9, color: T.red }}>{c.timeLeft}</span>
        </div>
      </div>

      <p style={{ fontSize: 11, color: T.t1, lineHeight: 1.5, fontWeight: 600, marginBottom: 10 }}>{c.title}</p>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, marginBottom: 10 }}>
        {[
          { label:'BASELINE',    value: c.baseline.accuracy },
          { label:'PARAM TARGET',value: '<50K' },
          { label:'SUBMISSIONS', value: c.submissions },
          { label:'PRIZE',       value: `${c.xp} XP` },
        ].map(({ label, value }) => (
          <div key={label} style={{
            padding: '6px 8px', background: T.void,
            border: `1px solid ${T.b1}`, borderRadius: 2,
          }}>
            <div style={{ fontSize: 7, color: T.t3, letterSpacing: '.1em' }}>{label}</div>
            <div style={{ fontSize: 12, color: T.gold, fontWeight: 800 }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Top submitter */}
      <div style={{
        padding: '6px 8px', background: `${T.green}08`,
        border: `1px solid ${T.green}22`, borderRadius: 2, marginBottom: 10,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: 7, color: T.t3, letterSpacing: '.1em', marginBottom: 2 }}>CURRENT LEADER</div>
          <span style={{ fontSize: 10, color: T.green, fontWeight: 700 }}>{c.topSubmitter.name}</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: T.t1, fontWeight: 700 }}>{c.topSubmitter.score}</div>
          <div style={{ fontSize: 8, color: T.t3 }}>{c.topSubmitter.params} params</div>
        </div>
      </div>

      <button
        onClick={() => setJoined(!joined)}
        style={{
          width: '100%', padding: '8px',
          background: joined ? `${T.green}20` : T.gold,
          border: `1px solid ${joined ? T.green : T.gold}`,
          color: joined ? T.green : T.void,
          fontSize: 10, fontWeight: 800, fontFamily: T.mono,
          cursor: 'pointer', borderRadius: 2, letterSpacing: '.08em',
          transition: 'all .15s',
        }}
      >{joined ? '✓ JOINED — Open Lab' : 'ACCEPT CHALLENGE →'}</button>
    </div>
  );
}

// ─── Trending tags ─────────────────────────────────────────────────────────────
function TrendingPanel() {
  return (
    <div style={{ background: T.deep, border: `1px solid ${T.b1}`, borderRadius: 4, padding: '13px 15px', marginBottom: 10 }}>
      <SectionHead label="TRENDING IN RESEARCH"/>
      {TRENDING_TAGS.map((t, i) => (
        <div key={t.tag} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 0', borderBottom: `1px solid ${T.b0}`,
          cursor: 'pointer',
        }}
          onMouseEnter={e => e.currentTarget.querySelector('.tag-label').style.color = T.cyan}
          onMouseLeave={e => e.currentTarget.querySelector('.tag-label').style.color = T.t2}
        >
          <span style={{ fontSize: 9, color: T.t4, width: 12, textAlign: 'right', flexShrink: 0 }}>#{i+1}</span>
          <span className="tag-label" style={{ flex: 1, fontSize: 11, color: T.t2, transition: 'color .12s', fontFamily: T.mono }}>
            {t.tag}
          </span>
          {t.hot && <span style={{ fontSize: 7, color: T.red }}>🔥</span>}
          <span style={{ fontSize: 9, color: T.green, flexShrink: 0 }}>{t.delta}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Online now ────────────────────────────────────────────────────────────────
function OnlinePanel({ onOpenChat }) {
  const online = Object.values(ENTITIES).filter(e => e.online);
  return (
    <div style={{ background: T.deep, border: `1px solid ${T.b1}`, borderRadius: 4, padding: '13px 15px', marginBottom: 10 }}>
      <SectionHead label="ONLINE NOW" action="VIEW ALL"/>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {online.map(e => {
          const isAI = e.type !== 'human';
          return (
            <div key={e.id} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '5px 6px', borderRadius: 3, cursor: 'pointer', transition: 'background .12s',
            }}
              onMouseEnter={el => el.currentTarget.style.background = T.surface}
              onMouseLeave={el => el.currentTarget.style.background = 'transparent'}
              onClick={() => onOpenChat?.(e.id)}
            >
              <Avatar entity={e} size={28}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 11, color: T.t1, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.name}
                  </span>
                  <Pill label={isAI ? (e.type === 'my_ai' ? 'MY AI' : 'AI') : 'HUMAN'} color={isAI ? (e.type === 'my_ai' ? T.cyan : T.violet) : T.green} small/>
                </div>
                <div style={{ fontSize: 9, color: T.t3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.role}</div>
              </div>
              <button
                onClick={ev => { ev.stopPropagation(); onOpenChat?.(e.id); }}
                style={{
                  padding: '3px 7px', background: 'transparent',
                  border: `1px solid ${T.b1}`, color: T.t3,
                  fontSize: 8, borderRadius: 2, cursor: 'pointer', fontFamily: T.mono,
                  flexShrink: 0, transition: 'all .12s',
                }}
                onMouseEnter={e2 => { e2.currentTarget.style.color = T.cyan; e2.currentTarget.style.borderColor = `${T.cyan}44`; }}
                onMouseLeave={e2 => { e2.currentTarget.style.color = T.t3; e2.currentTarget.style.borderColor = T.b1; }}
              >MSG</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Suggested collabs ─────────────────────────────────────────────────────────
function CollabsPanel({ onOpenChat }) {
  return (
    <div style={{ background: T.deep, border: `1px solid ${T.b1}`, borderRadius: 4, padding: '13px 15px', marginBottom: 10 }}>
      <SectionHead label="SUGGESTED COLLABORATORS"/>
      {SUGGESTED_COLLABS.map(({ entityId, reason, mutualExp }) => {
        const e = ENTITIES[entityId];
        if (!e) return null;
        return (
          <div key={entityId} style={{
            padding: '9px 0', borderBottom: `1px solid ${T.b0}`,
            display: 'flex', flexDirection: 'column', gap: 7,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar entity={e} size={28}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: T.t1, fontWeight: 600 }}>{e.name}</div>
                <div style={{ fontSize: 9, color: T.t3 }}>{e.role}</div>
              </div>
              {mutualExp > 0 && (
                <span style={{ fontSize: 8, color: T.violet }}>{mutualExp} mutual exp</span>
              )}
            </div>
            <p style={{ fontSize: 10, color: T.t3, lineHeight: 1.5 }}>{reason}</p>
            <div style={{ display: 'flex', gap: 5 }}>
              <button onClick={() => onOpenChat?.(entityId)} style={{
                flex: 1, padding: '5px 0',
                background: `${e.color}15`, border: `1px solid ${e.color}44`,
                color: e.color, fontSize: 9, fontWeight: 700,
                cursor: 'pointer', borderRadius: 2, fontFamily: T.mono,
              }}>MESSAGE</button>
              <button style={{
                flex: 1, padding: '5px 0',
                background: 'transparent', border: `1px solid ${T.b1}`,
                color: T.t3, fontSize: 9,
                cursor: 'pointer', borderRadius: 2, fontFamily: T.mono,
              }}>VIEW PROFILE</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Quick actions ─────────────────────────────────────────────────────────────
function QuickActions() {
  const actions = [
    { icon:'⬡', label:'Open a Lab',            color:T.cyan   },
    { icon:'💾', label:'Save Experiment',        color:T.green  },
    { icon:'⛓️', label:'Notarize on Polygon',    color:T.violet },
    { icon:'📓', label:'Export to Python',       color:T.gold   },
    { icon:'🎯', label:"Today's Challenge",      color:T.red    },
    { icon:'🔭', label:'Browse Public Registry', color:T.orange },
  ];
  return (
    <div style={{ background: T.deep, border: `1px solid ${T.b1}`, borderRadius: 4, padding: '13px 15px' }}>
      <SectionHead label="QUICK ACTIONS"/>
      {actions.map((a, i) => (
        <button key={i} style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          padding: '7px 8px', background: 'transparent',
          border: `1px solid ${T.b1}`, borderRadius: 2, marginBottom: 4,
          color: T.t2, fontSize: 11, cursor: 'pointer', fontFamily: T.mono,
          transition: 'all .12s', textAlign: 'left',
        }}
          onMouseEnter={e => { e.currentTarget.style.background = `${a.color}0a`; e.currentTarget.style.borderColor = `${a.color}44`; e.currentTarget.style.color = a.color; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = T.b1; e.currentTarget.style.color = T.t2; }}
        >
          <span>{a.icon}</span><span>{a.label}</span>
        </button>
      ))}
    </div>
  );
}

// ─── MAIN RIGHT PANEL ──────────────────────────────────────────────────────────
function mkRand(seed) {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => (s = (s * 16807) % 2147483647) / 2147483647;
}

function makeChurnCSV(rows = 350) {
  const r = mkRand(42);
  const out = ['age,income_k,tenure_m,support_calls,contract,churn'];
  const contracts = ['month_to_month', 'one_year', 'two_year'];
  for (let i = 0; i < rows; i++) {
    const age = Math.floor(18 + r() * 55);
    const income = Math.round((25 + r() * 120) * 10) / 10;
    const tenure = Math.floor(r() * 72);
    const calls = Math.floor(r() * 8);
    const contract = contracts[Math.floor(r() * contracts.length)];
    const score =
      0.02 * (55 - age) +
      0.015 * (65 - income) +
      0.03 * (18 - Math.min(18, tenure / 4)) +
      0.18 * calls +
      (contract === 'month_to_month' ? 0.8 : contract === 'one_year' ? 0.2 : -0.1);
    const p = 1 / (1 + Math.exp(-score));
    const churn = r() < p ? 1 : 0;
    out.push([age, income, tenure, calls, contract, churn].join(','));
  }
  return { name: 'synthetic_churn.csv', csvText: out.join('\n') };
}

function makeHousingCSV(rows = 350) {
  const r = mkRand(7);
  const out = ['sqft,bedrooms,age_yrs,city,price_k'];
  const cities = ['neo_bay', 'old_town', 'ridge', 'lake'];
  const cityMult = { neo_bay: 1.35, old_town: 1.1, ridge: 1.0, lake: 1.18 };
  for (let i = 0; i < rows; i++) {
    const sqft = Math.floor(600 + r() * 2800);
    const beds = Math.max(1, Math.min(6, Math.round(1 + (sqft / 700) + (r() - 0.5))));
    const age = Math.floor(r() * 70);
    const city = cities[Math.floor(r() * cities.length)];
    const base = 60 + sqft * 0.22 + beds * 18 - age * 1.1;
    const noise = (r() - 0.5) * 45;
    const price = Math.max(45, (base + noise) * (cityMult[city] || 1));
    out.push([sqft, beds, age, city, Math.round(price * 10) / 10].join(','));
  }
  return { name: 'synthetic_housing.csv', csvText: out.join('\n') };
}

function readExpwsByKey(storageKey) {
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeExpwsByKey(storageKey, data) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(data));
    window.dispatchEvent(new CustomEvent('dd-storage', { detail: { key: storageKey } }));
  } catch {
    // ignore quota errors
  }
}

function WorkspaceCard({ title, accent, children }) {
  return (
    <div style={{ background: T.deep, border: `1px solid ${T.b1}`, borderTop: `3px solid ${accent}`, borderRadius: 4, padding: '13px 15px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 8, color: accent, letterSpacing: '.14em', fontWeight: 800, fontFamily: T.mono }}>{title}</div>
      </div>
      {children}
    </div>
  );
}

function MiniBtn({ label, color, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 10px',
        border: `1px solid ${color}55`,
        background: `${color}18`,
        color,
        fontSize: 9,
        cursor: 'pointer',
        borderRadius: 2,
        fontFamily: T.mono,
        letterSpacing: '.08em',
        fontWeight: 800,
      }}
    >
      {label}
    </button>
  );
}

function SavedList({ items, emptyLabel, onOpen, onDelete }) {
  if (!items?.length) {
    return <div style={{ fontSize: 11, color: T.t3, lineHeight: 1.6 }}>{emptyLabel}</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((it) => (
        <div key={it.id} style={{ background: T.void, border: `1px solid ${T.b1}`, borderRadius: 3, padding: '9px 10px', display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: T.t1, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.title}</div>
            <div style={{ fontSize: 9, color: T.t3, marginTop: 2, fontFamily: T.mono, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {it.createdAt && <span>{new Date(it.createdAt).toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
              {it.meta?.rows != null && <span>{it.meta.rows.toLocaleString()} rows</span>}
              {it.meta?.cols != null && <span>{it.meta.cols} cols</span>}
              {it.meta?.chunks != null && <span>{it.meta.chunks.toLocaleString()} chunks</span>}
              {it.meta?.qualityScore != null && <span>Q {it.meta.qualityScore}</span>}
            </div>
          </div>
          {onOpen && (
            <button onClick={() => onOpen(it)} style={{ padding: '4px 8px', border: `1px solid ${T.b1}`, background: 'transparent', color: T.t2, fontSize: 9, borderRadius: 2, cursor: 'pointer', fontFamily: T.mono }}>
              OPEN
            </button>
          )}
          <button onClick={() => onDelete?.(it)} style={{ padding: '4px 8px', border: `1px solid ${T.red}44`, background: `${T.red}10`, color: T.red, fontSize: 9, borderRadius: 2, cursor: 'pointer', fontFamily: T.mono }}>
            DEL
          </button>
        </div>
      ))}
    </div>
  );
}

function SuggestedDatasets({ items, onLoad }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {items.map((d) => (
        <div key={d.id} style={{ background: T.void, border: `1px solid ${T.b1}`, borderRadius: 3, padding: '9px 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: T.t1, fontWeight: 800 }}>{d.title}</div>
            <div style={{ fontSize: 10, color: T.t3, marginTop: 2, lineHeight: 1.5 }}>{d.desc}</div>
          </div>
          <MiniBtn label="LOAD" color={d.color || T.cyan} onClick={() => onLoad(d)} />
        </div>
      ))}
    </div>
  );
}

export default function RightPanel({ onOpenChat, activeModule, isAuthenticated, authUser, onNavigate }) {
  // Prelogin: keep the marketing panel.
  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <PlatformCard/>
        <KeyFeaturesPanel/>
      </div>
    );
  }

  // Post-login: unified tabbed panel (MEMORY / DAG / SAVED)
  return (
    <>
      <RightPanelShell
        activeModule={activeModule}
        authUser={authUser}
        onNavigate={onNavigate}
      />
    </>
  );
}
